import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setSex, setWeight as updateWeight } from "../../redux/userSlice";
import Question from "../../components/Question";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Box, FormControl, TextField, useTheme } from "@mui/material";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";

import {
  continueButtonSx,
  inputLabelSx,
  textAreaSx,
  singleFieldSx,
} from "../../lib/uiformatting";
import ContinueButton from "../../components/ContinueButton";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";

dayjs.extend(utc);

export default function LastPeriodDate() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  const user = useSelector((state) => state.user);

  const normalizeDate = (val) => {
    if (!val) return null;
    if (dayjs.isDayjs(val)) return val.startOf("day");
    if (val instanceof Date && !Number.isNaN(val.getTime())) return dayjs(val).startOf("day");

    const s = String(val).trim();

    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return dayjs(s).startOf("day");

    // ISO / timezone-ish
    const hasTZ = /\bGMT\b|Z|[+-]\d{2}:?\d{2}/i.test(s) || s.includes("T");
    if (hasTZ) {
      const dUtc = dayjs.utc(s);
      return dayjs(dUtc.format("YYYY-MM-DD")).startOf("day");
    }

    // fallback
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : dayjs(d).startOf("day");
  };

  const [lmpDate, setLmpDate] = useState(normalizeDate(user.sex?.lmpDate));
  const [lmpFocused, setLmpFocused] = useState(false);
  const [lmpError, setLmpError] = useState(null);

  const [adultWeight, setAdultWeight] = useState(user.adultWeight || "");
  const [weightError, setWeightError] = useState("");
  const [weightFocused, setWeightFocused] = useState(false);

  const requiresLmpDate =
    (user.sex.sexAtBirth === "Female" || user.sex.sexAtBirth === "Prefer not to say") &&
    user.sex?.cycle !== "I don't have periods";

  const errorMessage = {
    invalidDate: "Please enter a valid date.",
    minDate: "Please enter a valid date.",
    maxDate: "Please enter a valid date.",
    disableFuture: "Please enter a date in the past.",
    required: "This field is required.",
  };

  const handleLmpChange = (newDate) => {
    if (!newDate) {
      setLmpDate(null);
      setLmpError("required");
      return;
    }

    if (newDate && newDate.isValid()) {
      const minAllowedDate = dayjs().subtract(2, "years");

      if (newDate.isAfter(dayjs(), "day")) {
        setLmpError("disableFuture");
        setLmpDate(null);
      } else if (newDate.isBefore(minAllowedDate, "day")) {
        setLmpError("minDate");
        setLmpDate(null);
      } else {
        setLmpDate(newDate.startOf("day"));
        setLmpError(null);
      }
    } else {
      setLmpDate(null);
      setLmpError("invalidDate");
    }
  };

  const canContinue = () => {
    const weightOk = Number(adultWeight) > 0 && !weightError;
    if (!requiresLmpDate) return weightOk;
    return !!lmpDate && !lmpError && weightOk;
  };

  const handleNext = () => {
    dispatch(
      setSex({
        ...user.sex,
        lmpDate: lmpDate ? lmpDate.format("YYYY-MM-DD") : null,
      })
    );
    dispatch(updateWeight(adultWeight));
    setPageAndDeferNavigate(dispatch, "/allergies", navigate);
  };

  const handleBack = () => {
    if (user.sex?.sexAtBirth === "Female" || user.sex?.sexAtBirth === "Prefer not to say") {
      setPageAndDeferNavigate(dispatch, "/maternity", navigate);
    } else {
      setPageAndDeferNavigate(dispatch, "/sex-at-birth", navigate);
    }
  };

  return (
    <Question
      onBack={handleBack}
      title="Please provide the necessary details for your case review"
      input={
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          {requiresLmpDate && (
            <Box sx={{ width: "100%", maxWidth: "450px" }}>
              <FormControl sx={{ width: "100%" }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    value={lmpDate}
                    onChange={handleLmpChange}
                    disableFuture
                    minDate={dayjs().subtract(2, "years")}
                    format="MM/DD/YYYY"
                    slotProps={{
                      textField: {
                        placeholder: "Last menstrual period - MM/DD/YYYY",
                        fullWidth: true,
                        error: !!lmpError,
                        helperText:
                          lmpFocused && !lmpError
                            ? "Helps us prescribe safely"
                            : lmpError
                              ? errorMessage[lmpError] || "Please enter a valid date."
                              : "",
                        sx: singleFieldSx,
                        name: "lmpDate",
                        onFocus: () => setLmpFocused(true),
                        onBlur: () => setLmpFocused(false),
                        InputLabelProps: {
                          sx: {
                            ...inputLabelSx,
                            fontSize: "12px",
                            "& .MuiInputLabel-shrink": {
                              backgroundColor: theme.palette.background.paper,
                            },
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </FormControl>
            </Box>
          )}

          <Box sx={{ width: "100%", mt: "2rem", maxWidth: "450px" }}>
            <FormControl sx={{ width: "100%" }}>
              <TextField
                label="What is your weight? (in lbs.)"
                type="number"
                inputProps={{ inputMode: "decimal" }}
                variant="outlined"
                color="secondary"
                sx={{
                  ...textAreaSx,
                  width: "100%",
                  fontSize: "12px",
                  "& .MuiInputLabel-root": {
                    backgroundColor: theme.palette.background.paper,
                    padding: "0 32px",
                    transition: "all 0.2s ease-in-out",
                  },
                }}
                InputLabelProps={{
                  sx: {
                    ...inputLabelSx,
                    fontSize: "12px",
                    "& .MuiInputLabel-shrink": {
                      backgroundColor: theme.palette.background.paper,
                    },
                  },
                }}
                autoComplete="off"
                value={adultWeight}
                onFocus={() => setWeightFocused(true)}
                onBlur={() => setWeightFocused(false)}
                onChange={(e) => {
                  const val = e.target.value;
                  setAdultWeight(val);

                  if (!val) {
                    setWeightError("This field is required.");
                  } else if (Number(val) <= 0 || Number(val) > 400) {
                    setWeightError("Please enter a valid weight.");
                  } else {
                    setWeightError("");
                  }
                }}
                error={weightError !== ""}
                helperText={weightFocused && !weightError ? "Used to help dose medications safely" : weightError}
              />
            </FormControl>
          </Box>
        </Box>
      }
      footer={
        <Box sx={{ ...continueButtonSx }}>
          <ContinueButton
            onClick={handleNext}
            title="Continue"
            selected={canContinue()}
            disabled={!canContinue()}
          />
        </Box>
      }
    />
  );
}
